import type {
  AIBasicModelMessage,
  AIConfig as AIConfigType,
  AIContext,
  AIContextSettings,
  AIProvider,
  ChatMessage,
} from '@tunasong/ai-lib'
import { aiConfigSchema } from '@tunasong/ai-lib'
import { AIConfigForm, AIConfigPreview } from '@tunasong/ai-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { nlpApi, skipToken, useThunkDispatch } from '@tunasong/redux'
import { isAudio, isImage, isStorageUrlsExpired } from '@tunasong/schemas'
import { useImage, useUserConfig, LinearProgress } from '@tunasong/ui-lib'
import type { ModelMessage } from 'ai'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'

interface StreamParams {
  prompt: ModelMessage[]
  model: string
  providerName: string
  requestId: string
}

export const AIConfig: FC = () => {
  const [userConfig, updateUserConfig, hasLoaded] = useUserConfig()
  const { profile } = graphHooks.useProfile()
  const dispatch = useThunkDispatch()

  // Text streaming state
  const [streamParams, setStreamParams] = useState<StreamParams | null>(null)
  const { currentData: textResult } = nlpApi.useCompleteStreamQuery(streamParams ?? skipToken)

  // Image preview state
  const [imageEntityId, setImageEntityId] = useState<string | null>(null)
  const [imagePending, setImagePending] = useState(false)
  const { entity: imageEntity } = graphHooks.useEntity(imageEntityId)
  const imageUrl = useImage(isImage(imageEntity) ? imageEntity : undefined, 'medium')

  // Music preview state
  const [musicEntityId, setMusicEntityId] = useState<string | null>(null)
  const [musicPending, setMusicPending] = useState(false)
  const { entity: musicEntity } = graphHooks.useEntity(musicEntityId)
  const musicUrl =
    isAudio(musicEntity) && !isStorageUrlsExpired(musicEntity.storageUrls) ? musicEntity.storageUrls?.url : undefined

  // When image entity becomes non-pending, clear the loading state
  useEffect(() => {
    if (imagePending && imageEntity && !imageEntity.pending) {
      setImagePending(false)
    }
  }, [imagePending, imageEntity])

  // When music entity becomes non-pending, clear the loading state
  useEffect(() => {
    if (musicPending && musicEntity && !musicEntity.pending) {
      setMusicPending(false)
    }
  }, [musicPending, musicEntity])

  const handleUpdateAIConfig = (aiConfig: AIConfigType) => updateUserConfig('ai', aiConfig)

  const handleGenerateText = useCallback(
    (params: { provider: AIProvider; model: string; prompt: ModelMessage[]; requestId: string }) => {
      setStreamParams({
        prompt: params.prompt,
        model: params.model,
        providerName: params.provider,
        requestId: params.requestId,
      })
    },
    []
  )

  const handleGenerateImage = useCallback(
    async (params: { description: string; model: string }) => {
      if (!profile) {
        return
      }
      setImagePending(true)
      try {
        const prompt: ChatMessage[] = [{ role: 'user', content: params.description }]
        const result = await dispatch(
          nlpApi.endpoints.image.initiate({
            prompt,
            chatModel: params.model,
            parentId: profile.id,
            name: 'Style Preview',
            size: '1024x1024',
          })
        ).unwrap()
        setImageEntityId(result.id)
      } catch {
        setImagePending(false)
      }
    },
    [dispatch, profile]
  )

  const handleGenerateMusic = useCallback(
    async (params: { description: string; model: string }) => {
      if (!profile) {
        return
      }
      setMusicPending(true)
      try {
        const prompt: AIBasicModelMessage[] = [{ role: 'user', content: params.description }]
        const result = await dispatch(
          nlpApi.endpoints.generateMusic.initiate({
            prompt,
            textModel: params.model,
            parentId: profile.id,
            name: 'Music Style Preview',
            durationSec: 15,
            instrumental: true,
          })
        ).unwrap()
        setMusicEntityId(result.id)
      } catch {
        setMusicPending(false)
      }
    },
    [dispatch, profile]
  )

  // Clear preview state when context changes
  const handleContextChange = useCallback(() => {
    setStreamParams(null)
    setImageEntityId(null)
    setImagePending(false)
    setMusicEntityId(null)
    setMusicPending(false)
  }, [])

  const renderPreview = useCallback(
    ({ context, settings }: { context: AIContext; settings: AIContextSettings }) => (
      <AIConfigPreview
        key={context}
        context={context}
        settings={settings}
        textResult={streamParams ? textResult : undefined}
        imageUrl={imageUrl}
        imagePending={imagePending}
        musicUrl={musicUrl}
        musicPending={musicPending}
        onGenerateText={handleGenerateText}
        onGenerateImage={handleGenerateImage}
        onGenerateMusic={handleGenerateMusic}
        onContextChange={handleContextChange}
      />
    ),
    [
      handleContextChange,
      handleGenerateImage,
      handleGenerateMusic,
      handleGenerateText,
      imagePending,
      imageUrl,
      musicPending,
      musicUrl,
      streamParams,
      textResult,
    ]
  )

  const aiConfigResult = aiConfigSchema.safeParse(userConfig.ai)

  return hasLoaded ? (
    <AIConfigForm defaultValue={aiConfigResult.data} onChange={handleUpdateAIConfig} renderPreview={renderPreview} />
  ) : (
    <LinearProgress />
  )
}

export default AIConfig
